import { PANEL_EVENTS } from '../shell/protocol';
/**
 * Panel Sortable — dependency-free drag-to-reorder (D1 decision).
 *
 * Replaces the dnd-kit surface the toolbar used (SortableContext /
 * useSortable / useDndContext / arrayMove) with a self-contained pointer
 * implementation: a 6px movement threshold keeps plain clicks and long-press
 * submenu opening unaffected, the dragged row rides the pointer while the
 * rows between the old and new slot slide over to make room, and the drop
 * reports a plain (activeId, from, to) callback instead of a dnd-kit event.
 * Keyboard reordering is not provided; the toolbar's customize menu covers
 * the same outcome through visibility/order controls.
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';

/** Pointer travel (px) before a press becomes a drag — mirrors the
 * reference app's PointerSensor activationConstraint.distance. */
export const DRAG_ACTIVATION_PX = 6;

type SortDirection = 'vertical' | 'horizontal';

interface DragState {
    activeId: string;
    from: number;
    to: number;
    /** Live pointer travel along the strip axis (px); 0 until activated */
    delta: number;
    /** True once the activation threshold is crossed */
    activated: boolean;
    /** Row size + gap along the axis: the distance siblings slide by */
    slideStep: number;
}

interface SortableContextValue {
    items: string[];
    direction: SortDirection;
    drag: DragState | null;
    registerItem: (id: string, el: HTMLElement) => void;
    unregisterItem: (id: string) => void;
    beginPress: (id: string, e: React.PointerEvent) => void;
    onSortEnd?: (activeId: string, from: number, to: number) => void;
}

const SortableContextValueContext = createContext<SortableContextValue | null>(
    null,
);

const useSortableContext = () => useContext(SortableContextValueContext);

export interface SortableContextProps {
    items: string[];
    /** Strip axis; defaults to 'vertical' */
    direction?: SortDirection;
    /** Called on drop when the active row landed on a new slot */
    onSortEnd?: (activeId: string, from: number, to: number) => void;
    children?: React.ReactNode;
}

/** Renders no wrapper DOM: consumers keep their exact layout structure. */
export const SortableContext: React.FC<SortableContextProps> = ({
    items,
    direction = 'vertical',
    onSortEnd,
    children,
}) => {
    const elements = useRef<Map<string, HTMLElement>>(new Map());
    const [drag, setDrag] = useState<DragState | null>(null);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const registerItem = useCallback((id: string, el: HTMLElement) => {
        elements.current.set(id, el);
    }, []);

    const unregisterItem = useCallback((id: string) => {
        elements.current.delete(id);
    }, []);

    const beginPress = useCallback(
        (id: string, e: React.PointerEvent) => {
            if (e.button !== 0) return;
            const list = itemsRef.current;
            const from = list.indexOf(id);
            if (from === -1) return;

            const axis = direction === 'horizontal' ? 'x' : 'y';
            const startPos = axis === 'x' ? e.clientX : e.clientY;
            // Rects frozen at press time: the drag geometry operates on the
            // layout the pointer came down on, not the one being translated.
            const rects = new Map<string, DOMRect>();
            for (const entryId of list) {
                const el = elements.current.get(entryId);
                if (el) rects.set(entryId, el.getBoundingClientRect());
            }
            const activeRect = rects.get(id);

            let state: DragState | null = null;
            let activated = false;
            let currentTo = from;

            const gapAfter = (entryId: string): number => {
                const rect = rects.get(entryId);
                if (!rect) return 0;
                const index = list.indexOf(entryId);
                const nextId = list[index + 1];
                const next = nextId ? rects.get(nextId) : undefined;
                if (!next) return 0;
                return axis === 'x'
                    ? next.left - (rect.left + rect.width)
                    : next.top - (rect.top + rect.height);
            };
            const itemSize = activeRect
                ? axis === 'x'
                    ? activeRect.width
                    : activeRect.height
                : 0;
            const slideStep = itemSize + (from >= 0 ? gapAfter(id) : 0);

            const computeTo = (pointer: number): number => {
                let to = from;
                for (let i = 0; i < list.length; i++) {
                    const otherId = list[i];
                    if (otherId === id) continue;
                    const rect = rects.get(otherId);
                    if (!rect) continue;
                    const mid = axis === 'x'
                        ? rect.left + rect.width / 2
                        : rect.top + rect.height / 2;
                    if (i < from && pointer < mid) to = Math.min(to, i);
                    if (i > from && pointer > mid) to = Math.max(to, i);
                }
                return to;
            };

            const handleMove = (ev: PointerEvent) => {
                const pointer = axis === 'x' ? ev.clientX : ev.clientY;
                const delta = pointer - startPos;
                if (!activated) {
                    if (Math.abs(delta) < DRAG_ACTIVATION_PX) return;
                    activated = true;
                    // Toolbar-wide drag state: grab cursor everywhere and any
                    // open long-press submenu closes immediately (drag and the
                    // click+wait gesture never mix).
                    document.body.classList.add('toolbar-item-dragging');
                    document.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.toolbarDragActive),
                    );
                }
                currentTo = computeTo(pointer);
                state = {
                    activeId: id,
                    from,
                    to: currentTo,
                    delta,
                    activated: true,
                    slideStep,
                };
                setDrag(state);
            };

            const cleanup = () => {
                document.body.classList.remove('toolbar-item-dragging');
                window.removeEventListener('pointermove', handleMove);
                window.removeEventListener('pointerup', handleUp);
                window.removeEventListener('pointercancel', handleUp);
                setDrag(null);
            };

            const handleUp = () => {
                if (activated && currentTo !== from) {
                    onSortEnd?.(id, from, currentTo);
                }
                cleanup();
            };

            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', handleUp);
            window.addEventListener('pointercancel', handleUp);
        },
        [direction, onSortEnd],
    );

    const value = useMemo<SortableContextValue>(
        () => ({
            items,
            direction,
            drag,
            registerItem,
            unregisterItem,
            beginPress,
            onSortEnd,
        }),
        [items, direction, drag, registerItem, unregisterItem, beginPress, onSortEnd],
    );

    return (
        <SortableContextValueContext.Provider value={value}>
            {children}
        </SortableContextValueContext.Provider>
    );
};

export interface UseSortableResult {
    attributes: Record<string, unknown>;
    listeners: { onPointerDown: (e: React.PointerEvent) => void };
    setNodeRef: (el: HTMLElement | null) => void;
    isDragging: boolean;
    transform: { x: number; y: number } | null;
    transition: string | undefined;
}

export function useSortable({
    id,
    disabled = false,
}: {
    id: string;
    disabled?: boolean;
}): UseSortableResult {
    const ctx = useSortableContext();
    const drag = ctx?.drag ?? null;

    const setNodeRef = useCallback(
        (el: HTMLElement | null) => {
            if (!ctx) return;
            if (el) ctx.registerItem(id, el);
            else ctx.unregisterItem(id);
        },
        [ctx, id],
    );

    const isDragging = drag?.activeId === id && drag.activated;

    let transform: { x: number; y: number } | null = null;
    if (ctx && drag && drag.activated) {
        const axis = ctx.direction === 'horizontal' ? 'x' : 'y';
        const index = ctx.items.indexOf(id);
        const { from, to } = drag;
        if (isDragging) {
            transform = axis === 'x'
                ? { x: drag.delta, y: 0 }
                : { x: 0, y: drag.delta };
        } else if (index !== -1 && from !== to) {
            // Slide the rows between the old and new slot over to make room
            const slidesToward = to > from
                ? index > from && index <= to
                : index >= to && index < from;
            if (slidesToward) {
                const shift = to > from ? -1 : 1;
                const offset = shift * drag.slideStep;
                transform = axis === 'x'
                    ? { x: offset, y: 0 }
                    : { x: 0, y: offset };
            }
        }
    }

    const listeners = useMemo(
        () => ({
            onPointerDown: (e: React.PointerEvent) => {
                if (disabled || !ctx) return;
                ctx.beginPress(id, e);
            },
        }),
        [ctx, disabled, id],
    );

    return {
        attributes: { 'data-sortable-id': id },
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition: drag?.activated ? 'transform 150ms ease' : undefined,
    };
}

/** True while any row inside the surrounding SortableContext is being
 * dragged (replacement for dnd-kit's useDndContext().active). */
export function useSortableDragActive(): boolean {
    const ctx = useSortableContext();
    return Boolean(ctx?.drag?.activated);
}

/** Immutably move `from` to `to` inside `array` (replacement for dnd-kit's
 * arrayMove). */
export function arrayMove<T>(array: T[], from: number, to: number): T[] {
    const next = [...array];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
}
